import React, {useCallback, useState} from 'react';
import { Table, Tag, DatePicker, Button, Input, message} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { GET_PICKUP_PROGRESS_NEW, GetPickupProgressNewResponse } from './graphql/queries/getPickupProgressNew';
import { useWarehouseContext } from "../../../contexts/WarehouseContext";
import IndicatorCard from "../../../components/IndicatorCard";
import dayjs, {Dayjs} from "dayjs";
import PickupTaskDetailDrawer from '../pickup-task/components/PickupTaskDetailDrawer';
import {PickupDataProvider} from '../pickup-task/contexts/PickupDataContext';
import ProgressDownloadModal from './components/ProgressDownloadModal';
import { useTranslation } from 'react-i18next';
import { timestampToDate } from '../../../utils/dateTimeUtils';
import { normalizeTimezone } from '../../../utils/timezoneUtils';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useFilterLabelClassName } from '../../../utils/languageUtils';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { GENERATE_PICKUP_PROGRESS_DOWNLOAD_REPORT } from './graphql/queries/generatePickupProgressDownloadReport';
import PickupFailedAppointmentDrawer from './components/PickupFailedAppointmentDrawer';
import {
    GET_PICKUP_FAILED_APPOINTMENTS,
    GetPickupFailedAppointmentsResponse,
    GET_SUSPECTED_FAKE_APPOINTMENTS,
    GetSuspectedFakeAppointmentsResponse,
    GET_PICKUP_NOT_ONLINE_SUMMARY,
    GetPickupNotOnlineSummaryResponse,
    GET_PICKUP_UNSCANNABLE_APPOINTMENTS,
    GetPickupUnscannableAppointmentsResponse,
} from './graphql/queries/getPickupFailedAppointments';
import PartialPickupAppointmentDrawer from './components/PartialPickupAppointmentDrawer';
import { usePickupStartPartialGray } from '../../../hooks/usePickupStartPartialGray';
import {
    GET_PARTIAL_PICKUP_SUMMARY,
    GetPartialPickupSummaryResponse,
} from './graphql/queries/getPartialPickupAppointments';
import { usePickupTrackingScanGray } from '../../../hooks/usePickupTrackingScanGray';

dayjs.extend(utc);
dayjs.extend(timezone);

const ProgressPage: React.FC = () => {
    const { currentWarehouse: warehouse } = useWarehouseContext();
    const {t} = useTranslation();
    const [selectedTaskDate, setSelectedTaskDate] = useState<Dayjs | null>(dayjs());
    const [currentWarehouseId, setCurrentWarehouseId] = useState<string | null>(warehouse?.id || null);
    const taskDateLabelClassName = useFilterLabelClassName("mr-2 whitespace-nowrap flex-shrink-0");

    const [loadProgress, { data, loading }] = useLazyQuery<GetPickupProgressNewResponse>(GET_PICKUP_PROGRESS_NEW, {
        fetchPolicy: 'network-only', // 强制从网络获取，确保数据隔离
    });

    // 获取格式化的日期字符串
    const taskDateStr = selectedTaskDate ? selectedTaskDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');

    // 任务状态筛选
    const [taskStatusFilter, setTaskStatusFilter] = useState<string>('ALL');

    // 分页状态
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    // 监听warehouse变化，确保warehouse切换时立即刷新数据
    React.useEffect(() => {
        if (warehouse?.id) {
            const warehouseId = warehouse.id;
            if (warehouseId !== currentWarehouseId) {
                console.log('🔄 Warehouse changed:', { from: currentWarehouseId, to: warehouseId });
                setCurrentWarehouseId(warehouseId);
                setTaskStatusFilter('ALL'); // 清除筛选状态

                // 立即加载新warehouse的数据
                console.log('🔄 Loading pickup progress for new warehouse:', { warehouseId, taskDate: taskDateStr });
                loadProgress({
                    variables: {
                        warehouseId: warehouseId,
                        taskDate: taskDateStr
                    }
                });
            }
        }
    }, [warehouse?.id, currentWarehouseId, taskDateStr, loadProgress]);

    // 监听日期变化，当日期改变时刷新数据
    React.useEffect(() => {
        if (currentWarehouseId) {
            console.log('🔄 Date changed, reloading data:', { warehouseId: currentWarehouseId, taskDate: taskDateStr });
            loadProgress({
                variables: {
                    warehouseId: currentWarehouseId,
                    taskDate: taskDateStr
                }
            });
        }
    }, [taskDateStr, currentWarehouseId, loadProgress]);

    // 手动刷新数据函数
    const refresh = useCallback(() => {
        if (currentWarehouseId) {
            console.log('🔄 Manual refresh:', { warehouseId: currentWarehouseId, taskDate: taskDateStr });
            loadProgress({
                variables: {
                    warehouseId: currentWarehouseId,
                    taskDate: taskDateStr
                }
            });
        }
    }, [currentWarehouseId, taskDateStr, loadProgress]);

    const snapshot = data?.getPickupProgressNew;
    const tasks = snapshot?.tasks || [];
    const summary = snapshot?.summary;

    const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [progressFileName, setProgressFileName] = useState('');
    const [progressDownloadVisible, setProgressDownloadVisible] = useState(false);
    const [generateProgressReport] = useLazyQuery(GENERATE_PICKUP_PROGRESS_DOWNLOAD_REPORT, {
        fetchPolicy: 'network-only',
    });

    // 揽收质量反馈：揽收失败预约单抽屉 + 计数（计数复用抽屉查询，pageSize=1 只取 totalElements）
    const [failedDrawerVisible, setFailedDrawerVisible] = useState(false);
    const [loadFailedCount, { data: failedCountData }] = useLazyQuery<GetPickupFailedAppointmentsResponse>(
        GET_PICKUP_FAILED_APPOINTMENTS,
        { fetchPolicy: 'network-only' },
    );
    React.useEffect(() => {
        if (currentWarehouseId) {
            loadFailedCount({
                variables: { filter: { hubId: Number(currentWarehouseId), pickupDate: taskDateStr, pageNum: 1, pageSize: 1 } },
            });
        }
    }, [currentWarehouseId, taskDateStr, loadFailedCount]);
    const failedCount = failedCountData?.getPickupFailedAppointments?.totalElements || 0;

    // 虚假上门预约单抽屉 + 计数/每任务聚合（数据源 POP 疑似虚假单；本期回调关则为空）
    const [fakeDrawerVisible, setFakeDrawerVisible] = useState(false);
    // 抽屉打开时的司机预筛选：主表格某任务列点击带入该任务司机；卡片入口则清空看全部
    const [fakeInitialDriver, setFakeInitialDriver] = useState<string | undefined>(undefined);
    // 一次查询同时得到：卡片总数(totalElements) + 主表格每任务聚合(content)。
    // pageSize 与后端 MAX_PAGE_SIZE 对齐(200)；总数超过它时 content 被后端截断、聚合可能少计，告警兜底。
    const FAKE_LIST_CAP = 200;
    const [loadFakeList, { data: fakeListData }] = useLazyQuery<GetSuspectedFakeAppointmentsResponse>(
        GET_SUSPECTED_FAKE_APPOINTMENTS,
        { fetchPolicy: 'network-only' },
    );
    React.useEffect(() => {
        if (currentWarehouseId) {
            loadFakeList({
                variables: { filter: { hubId: Number(currentWarehouseId), pickupDate: taskDateStr, pageNum: 1, pageSize: FAKE_LIST_CAP } },
            });
        }
    }, [currentWarehouseId, taskDateStr, loadFakeList]);
    const fakeCount = fakeListData?.getSuspectedFakeAppointments?.totalElements || 0;
    const fakeCountByTask = React.useMemo(() => {
        const m = new Map<string, number>();
        const list = fakeListData?.getSuspectedFakeAppointments;
        (list?.content || []).forEach((it) => {
            if (it.taskNumber) m.set(it.taskNumber, (m.get(it.taskNumber) || 0) + 1);
        });
        if (list && list.totalElements > FAKE_LIST_CAP) {
            console.warn(`虚假上门单数超过 ${FAKE_LIST_CAP}，主表格每任务列可能少计`);
        }
        return m;
    }, [fakeListData]);

    // 运单号扫描灰度：命中的 hub 才展示「预约单上网情况」与「未上网预约单数」，
    // 与司机端扫描模块共用同一份名单（未命中的 hub 根本没有扫描数据，展示出来全是 0 反而误导）
    const { trackingScanEnabled } = usePickupTrackingScanGray();
    const showOnlineStatus = trackingScanEnabled(currentWarehouseId);

    // 预约单上网情况：未上网汇总（顶部数字 + 主表格每任务聚合，一次查询喂两处）
    const [notOnlineDrawerVisible, setNotOnlineDrawerVisible] = useState(false);
    // 抽屉打开时的司机预筛选：主表格某任务列点击带入该任务司机；卡片入口则清空看全部
    const [notOnlineInitialDriver, setNotOnlineInitialDriver] = useState<string | undefined>(undefined);
    const [loadNotOnlineSummary, { data: notOnlineSummaryData }] = useLazyQuery<GetPickupNotOnlineSummaryResponse>(
        GET_PICKUP_NOT_ONLINE_SUMMARY,
        { fetchPolicy: 'network-only' },
    );
    React.useEffect(() => {
        // 未命中灰度不发查询：省两次 Dubbo 穿透，且这些 hub 本就没有扫描数据
        if (currentWarehouseId && showOnlineStatus) {
            loadNotOnlineSummary({ variables: { hubId: Number(currentWarehouseId), pickupDate: taskDateStr } });
        }
    }, [currentWarehouseId, taskDateStr, showOnlineStatus, loadNotOnlineSummary]);
    const notOnlineSummary = notOnlineSummaryData?.getPickupNotOnlineSummary;
    // 顶部数字取后端 total，不用每任务聚合求和：未挂揽收任务的预约单不在聚合里，求和会少计
    const notOnlineCount = notOnlineSummary?.total || 0;
    const notOnlineCountByTask = React.useMemo(() => {
        const m = new Map<string, number>();
        (notOnlineSummary?.countByTask || []).forEach((it) => m.set(it.taskNumber, it.count));
        return m;
    }, [notOnlineSummary]);

    // 部分揽收成功预约单：汇总（质量反馈模块数字 + 主表格每任务聚合，一次查询喂两处）
    // 灰度：未命中的 hub 不展示部分揽收相关指标与抽屉入口——该 hub 的司机端还没有部分揽收选择，
    // 指标恒为 0，露出来只会让运营以为数据异常
    const { startPartialEnabled } = usePickupStartPartialGray();
    const partialPickupGrayEnabled = startPartialEnabled(warehouse?.id);
    const [partialPickupDrawerVisible, setPartialPickupDrawerVisible] = useState(false);
    const [partialPickupInitialDriver, setPartialPickupInitialDriver] = useState<string | undefined>(undefined);
    const [loadPartialPickupSummary, { data: partialPickupSummaryData }] = useLazyQuery<GetPartialPickupSummaryResponse>(
        GET_PARTIAL_PICKUP_SUMMARY,
        { fetchPolicy: 'network-only' },
    );
    React.useEffect(() => {
        if (currentWarehouseId) {
            loadPartialPickupSummary({ variables: { hubId: Number(currentWarehouseId), pickupDate: taskDateStr } });
        }
    }, [currentWarehouseId, taskDateStr, loadPartialPickupSummary]);
    const partialPickupSummary = partialPickupSummaryData?.getPartialPickupSummary;
    // 同未上网：总数取后端 total，不对每任务聚合求和——未挂任务的预约单不在聚合里
    const partialPickupCount = partialPickupSummary?.total || 0;
    const partialPickupCountByTask = React.useMemo(() => {
        const m = new Map<string, number>();
        (partialPickupSummary?.countByTask || []).forEach((it) => m.set(it.taskNumber, it.count));
        return m;
    }, [partialPickupSummary]);

    // 无法扫描上报预约单抽屉 + 计数（计数复用抽屉查询，pageSize=1 只取 totalElements）
    const [unscannableDrawerVisible, setUnscannableDrawerVisible] = useState(false);
    const [loadUnscannableCount, { data: unscannableCountData }] = useLazyQuery<GetPickupUnscannableAppointmentsResponse>(
        GET_PICKUP_UNSCANNABLE_APPOINTMENTS,
        { fetchPolicy: 'network-only' },
    );
    React.useEffect(() => {
        if (currentWarehouseId && showOnlineStatus) {
            loadUnscannableCount({
                variables: { filter: { hubId: Number(currentWarehouseId), pickupDate: taskDateStr, pageNum: 1, pageSize: 1 } },
            });
        }
    }, [currentWarehouseId, taskDateStr, showOnlineStatus, loadUnscannableCount]);
    const unscannableCount = unscannableCountData?.getPickupUnscannableAppointments?.totalElements || 0;

    // 从后端快照直接获取汇总数据，无需前端计算
    const taskStatusStats = {
        total: summary?.tasks.total || 0,
        draft: summary?.tasks.draft || 0,
        published: summary?.tasks.published || 0,
        inProgress: summary?.tasks.inProgress || 0,
        completed: summary?.tasks.completed || 0,
        cancelled: summary?.tasks.cancelled || 0
    };

    const filteredTasks = React.useMemo(() => {
        if (taskStatusFilter === 'ALL') return tasks;
        return tasks.filter(t => t.status === taskStatusFilter);
    }, [tasks, taskStatusFilter]);

    // 构建表格数据
    const tableData = React.useMemo(() => {
        return filteredTasks.map((t, index) => ({
            key: `${t.taskNumber}-${index}`,
            taskId: t.taskId,
            taskNumber: t.taskNumber,
            driver: t.driver,
            driverPhoneNumber: t.driverPhoneNumber,
            driverCountryCode: t.driverCountryCode,
            vehicleNumber: t.vehicleNumber,
            licensePlate: t.licensePlate,
            expDepartureTime: t.expDepartureTime,
            actDepartureTime: t.actDepartureTime,
            appointmentOnTimeRate: t.appointmentOnTimeRate,
            expectedParcel: t.expectedParcel,
            pickedUpParcel: t.pickedUpParcel,
            status: t.status
        }));
    }, [filteredTasks]);

    const pagedTableData = React.useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        return tableData.slice(start, end);
    }, [tableData, currentPage, pageSize]);

    // 当筛选条件变化时，重置到第一页
    React.useEffect(() => {
        setCurrentPage(1);
    }, [taskStatusFilter]);

    // 处理分页变化（遵循项目统一模式：切换每页条数时不重置页码）
    const handlePageChange = (page: number, newPageSize?: number) => {
        setCurrentPage(page);
        if (newPageSize && newPageSize !== pageSize) {
            setPageSize(newPageSize);
        }
    };

    // 格式化UTC时间戳为hub时区的时间显示
    const formatTimestampToHubTime = (timestamp: number | string): string => {
        // 处理空值
        if (!timestamp) return '--';

        try {
            // 转换为数字，处理字符串类型的时间戳
            const numericTimestamp = Number(timestamp);

            // 验证时间戳是否为有效数字
            if (isNaN(numericTimestamp) || numericTimestamp <= 0) {
                console.warn('Invalid timestamp:', timestamp);
                return '--';
            }

            // 获取hub的时区
            const hubTimezone = warehouse?.timezone || 'UTC-7';
            const normalizedTimezone = normalizeTimezone(hubTimezone);

            // UTC时间戳 -> UTC dayjs对象 -> 转换到hub时区
            const date = timestampToDate(numericTimestamp);
            const result = dayjs.utc(date).tz(normalizedTimezone);

            // 验证dayjs对象是否有效
            if (!result.isValid()) {
                console.warn('Invalid date after conversion:', timestamp);
                return '--';
            }

            return result.format('YYYY/MM/DD HH:mm');
        } catch (error) {
            // 捕获任何转换过程中的异常
            console.error('Error formatting timestamp:', timestamp, error);
            return '--';
        }
    };

    // 格式化最后更新时间（使用浏览器本地时区）
    const getLastUpdateTime = () => {
        // 如果没有后端返回的更新时间，返回默认值
        if (!snapshot?.lastUpdateAt) return '--';

        try {
            // 解析ISO时间字符串并使用浏览器本地时区
            const result = dayjs(snapshot.lastUpdateAt);

            // 验证dayjs对象是否有效
            if (!result.isValid()) {
                console.warn('Invalid lastUpdateAt:', snapshot.lastUpdateAt);
                return '--';
            }

            // 使用与delivery页面相同的格式：MM-DD HH:mm:ss
            return result.format('MM-DD HH:mm:ss');
        } catch (error) {
            console.error('Error formatting lastUpdateAt:', error);
            return '--';
        }
    };

    // 处理数据报表下载
    const handleReportDownload = async () => {
        if (!currentWarehouseId) {
            message.error('Please select a warehouse first');
            return;
        }

        try {
            const selectedDate = (selectedTaskDate || dayjs());
            const { data } = await generateProgressReport({
                variables: {
                    filters: {
                        warehouseId: Number(currentWarehouseId),
                        confirmTimeStart: selectedDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
                        confirmTimeEnd: selectedDate.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
                    },
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
            });
            const fileName = data?.generatePickupProgressDownloadReport;
            if (!fileName) {
                message.error('Failed to start report generation task');
                return;
            }
            setProgressFileName(fileName);
            setProgressDownloadVisible(true);
        } catch (error: any) {
            message.error(error?.message || 'Failed to start report generation task');
        }
    };

    // 通用文本搜索列配置
    const getTextSearchColumn = (dataIndex: string, placeholder: string) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={placeholder}
                    value={selectedKeys?.[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <div className="flex gap-2">
                    <Button type="primary" size="small" onClick={() => confirm()}>
                        {t('components.button.search')}
                    </Button>
                    <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>
                        {t('components.button.reset')}
                    </Button>
                </div>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value: any, record: any) => (
            String(record[dataIndex] ?? '').toLowerCase().includes(String(value).toLowerCase())
        )
    });

    return (
        <PickupDataProvider>
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                <div className="mb-2 py-2 pl-0">
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                            <div className="flex items-center">
                                <span className={taskDateLabelClassName}>{t('pickup.progress.filters.taskDate')}:</span>
                                <div className="flex-1">
                                    <DatePicker
                                        value={selectedTaskDate}
                                        onChange={setSelectedTaskDate}
                                        format="YYYY/MM/DD"
                                        placeholder={dayjs().format("YYYY/MM/DD")}
                                        disabledDate={(current) => {
                                            const today = dayjs();
                                            return current && current.isAfter(today, 'day');
                                        }}
                                        allowClear={false}
                                        size="middle"
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* 统计卡片 - 直接使用后端返回的汇总数据 */}
                <div className="flex gap-12 mb-4">
                    {/* 任务状态卡片 */}
                    <IndicatorCard
                        title={t('pickup.progress.filters.tasks')}
                        items={[
                            { label: t('pickup.progress.filters.total'), value: taskStatusStats.total,
                                onClick: () => setTaskStatusFilter('ALL') , showArrow: true,},
                            { label: t('pickup.progress.filters.draft'), value: taskStatusStats.draft,
                                onClick: () => setTaskStatusFilter('DRAFT') , showArrow: true,},
                            { label: t('pickup.progress.filters.published'), value: taskStatusStats.published,
                                onClick: () => setTaskStatusFilter('PUBLISHED') , showArrow: true,},
                            { label: t('pickup.progress.filters.inProgress'), value: taskStatusStats.inProgress,
                                onClick: () => setTaskStatusFilter('IN_PROGRESS') , showArrow: true,},
                            { label: t('pickup.progress.filters.completed'), value: taskStatusStats.completed,
                                onClick: () => setTaskStatusFilter('COMPLETED') , showArrow: true,},
                            { label: t('pickup.progress.filters.cancelled'), value: taskStatusStats.cancelled,
                                onClick: () => setTaskStatusFilter('CANCELLED') , showArrow: true,},
                        ]}
                    />
                    {/* 准时率卡片 */}
                    <IndicatorCard
                        title={t('pickup.progress.filters.onTimeRate')}
                        items={[
                            { label: t('pickup.progress.filters.appointments'), value: summary?.onTimeRate.appointments || '0.0%', showArrow: false },
                            { label: t('pickup.progress.filters.parcels'), value: summary?.onTimeRate.parcels || '0.0%', showArrow: false },
                        ]}
                    />
                    {/* 包裹统计 */}
                    <IndicatorCard
                        title={t('pickup.progress.filters.parcels')}
                        items={[
                            { label: t('pickup.progress.filters.expected'), value: summary?.parcels.expected || '0', showArrow: false },
                            { label: t('pickup.progress.filters.pickedUp'), value: summary?.parcels.pickedUp || '0', showArrow: false },
                        ]}
                    />
                    {/* 揽收质量反馈 */}
                    <IndicatorCard
                        title={t('pickup.progress.qualityFeedback.title')}
                        items={[
                            // 揽收失败数用默认(黑)色，仅「疑似虚假揽收数」保持红色以突出风险
                            { label: t('pickup.progress.qualityFeedback.failedCount'), value: failedCount, onClick: () => setFailedDrawerVisible(true), showArrow: true },
                            { label: t('pickup.progress.qualityFeedback.fakeCount'), value: fakeCount, color: fakeCount > 0 ? 'red' : undefined, onClick: () => { setFakeInitialDriver(undefined); setFakeDrawerVisible(true); }, showArrow: true },
                            ...(partialPickupGrayEnabled ? [{ label: t('pickup.progress.partialPickup.count'), value: partialPickupCount, color: partialPickupCount > 0 ? ('red' as const) : undefined, onClick: () => { setPartialPickupInitialDriver(undefined); setPartialPickupDrawerVisible(true); }, showArrow: true }] : []),
                        ]}
                    />
                    {/* 预约单上网情况（运单扫描灰度命中的 hub 才展示） */}
                    {showOnlineStatus && (
                    <IndicatorCard
                        title={t('pickup.progress.onlineStatus.title')}
                        items={[
                            { label: t('pickup.progress.onlineStatus.unscannableCount'), value: unscannableCount, color: unscannableCount > 0 ? 'red' : undefined, onClick: () => setUnscannableDrawerVisible(true), showArrow: true },
                            { label: t('pickup.progress.onlineStatus.notOnlineCount'), value: notOnlineCount, color: notOnlineCount > 0 ? 'red' : undefined, onClick: () => { setNotOnlineInitialDriver(undefined); setNotOnlineDrawerVisible(true); }, showArrow: true },
                        ]}
                    />
                    )}
                </div>

                {/* 数据更新时间提示 */}
                <div className="mb-4">
                    <p className="text-sm text-red-500">
                        {t('pickup.progress.dataUpdateTip')} {getLastUpdateTime()}
                    </p>
                </div>

                {/* Data Report Download Button */}
                <div className="flex justify-end gap-4 mb-3 pl-0">
                <Button
                    type="primary"
                    onClick={handleReportDownload}
                    className="w-auto min-w-24"
                    style={{
                        backgroundColor: '#8B5CF6',
                        borderColor: '#8B5CF6'
                    }}
                >
                    <span className="font-bold">{t('pickup.appointment.buttons.reportDownload') || 'Data Report'}</span>
                </Button>
            </div>

            <div>
                <Table
                    loading={loading}
                    rowKey={(r) => r.key}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    tableLayout="auto"
                    className="pickup-task-table mb-4"
                    dataSource={pagedTableData}
                    pagination={false}
                    columns={[
                        { 
                          title: t('pickup.progress.columns.taskNumber'), 
                          dataIndex: 'taskNumber', 
                          width: 160, 
                          align: 'left' as const,
                          ellipsis: false,
                          ...getTextSearchColumn('taskNumber', t('pickup.progress.placeholders.searchTaskNumber')),
                          render: (text: string, record: any) => (
                            <Button
                                type="link"
                                style={{paddingLeft: 0}}
                                onClick={() => {
                                    setEditingTaskId(record.taskId);
                                    setDrawerVisible(true);
                                }}
                            >
                                <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
                            </Button>
                          )
                        },
                        { 
                          title: t('pickup.progress.columns.driver'), 
                          dataIndex: 'driver', 
                          width: 260, 
                          align: 'left' as const,
                          ellipsis: false,
                          ...getTextSearchColumn('driver', t('pickup.progress.placeholders.searchDriver')),
                          render: (_: any, record: any) => (
                            <div style={{ whiteSpace: 'nowrap' }}>
                                <span>{record.driver || '--'}</span>
                                <span className="text-gray-400 text-xs">
                                    {record.driverPhoneNumber
                                        ? ` ${record.driverCountryCode ? `+${record.driverCountryCode} `: ''}${record.driverPhoneNumber}`
                                        : ' --'}
                                </span>
                            </div>
                          )
                        },
                        { 
                          title: t('pickup.progress.columns.vehicleNumber'), 
                          dataIndex: 'licensePlate', 
                          width: 160, 
                          align: 'left' as const,
                          ellipsis: false,
                          render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '--'}</span>
                        },
                        { 
                          title: t('pickup.progress.columns.expDepartureTime'), 
                          dataIndex: 'expDepartureTime', 
                          width: 200, 
                          align: 'left' as const,
                          ellipsis: false,
                          render: (text: string) => {
                            const formatted = formatTimestampToHubTime(text);
                            return <span style={{ whiteSpace: 'nowrap' }}>{formatted}</span>;
                          }
                        },
                        {
                          title: t('pickup.progress.columns.actDepartureTime'),
                          dataIndex: 'actDepartureTime',
                          width: 200,
                          align: 'left' as const,
                          ellipsis: false,
                          render: (text: string) => {
                            const formatted = formatTimestampToHubTime(text);
                            return <span style={{ whiteSpace: 'nowrap' }}>{formatted}</span>;
                          }
                        },
                        {
                          title: t('pickup.progress.columns.appointmentOnTimeRate'),
                          dataIndex: 'appointmentOnTimeRate',
                          width: 180,
                          align: 'left' as const,
                          ellipsis: false,
                          render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                        },
                        {
                          title: t('pickup.progress.qualityFeedback.fakeColumn'),
                          dataIndex: 'taskNumber',
                          key: 'fakeCount',
                          width: 160,
                          align: 'left' as const,
                          ellipsis: false,
                          render: (_: any, record: any) => {
                            const c = fakeCountByTask.get(record.taskNumber) || 0;
                            return c > 0 ? (
                              <Button type="link" style={{ paddingLeft: 0, color: '#ff4d4f' }} onClick={() => { setFakeInitialDriver(record.driver || undefined); setFakeDrawerVisible(true); }}>
                                <span style={{ whiteSpace: 'nowrap' }}>{c}</span>
                              </Button>
                            ) : (
                              <span style={{ whiteSpace: 'nowrap' }}>0</span>
                            );
                          }
                        },
                        // 未上网预约单数：仅运单扫描灰度命中的 hub 出这一列，
                        // 未命中的 hub 没有扫描数据，出一列全 0 反而误导
                        ...(showOnlineStatus ? [{
                          // 任务维度聚合，不为零标红且可点击（带入该任务司机筛选）
                          title: t('pickup.progress.onlineStatus.notOnlineColumn'),
                          dataIndex: 'taskNumber',
                          key: 'notOnlineCount',
                          width: 170,
                          align: 'left' as const,
                          ellipsis: false,
                          render: (_: any, record: any) => {
                            const count = notOnlineCountByTask.get(record.taskNumber) || 0;
                            return count > 0 ? (
                              <Button type="link" style={{ paddingLeft: 0, color: '#ff4d4f' }} onClick={() => { setNotOnlineInitialDriver(record.driver || undefined); setNotOnlineDrawerVisible(true); }}>
                                <span style={{ whiteSpace: 'nowrap' }}>{count}</span>
                              </Button>
                            ) : (
                              <span style={{ whiteSpace: 'nowrap' }}>0</span>
                            );
                          }
                        }] : []),
                        {
                          // 部分揽收成功预约单数：任务维度聚合，非 0 标红且可点击（带入该任务司机筛选）
                          title: t('pickup.progress.partialPickup.column'),
                          dataIndex: 'taskNumber',
                          key: 'partialPickupCount',
                          width: 190,
                          align: 'left' as const,
                          ellipsis: false,
                          render: (_: any, record: any) => {
                            const count = partialPickupCountByTask.get(record.taskNumber) || 0;
                            return count > 0 ? (
                              <Button type="link" style={{ paddingLeft: 0, color: '#ff4d4f' }} onClick={() => { setPartialPickupInitialDriver(record.driver || undefined); setPartialPickupDrawerVisible(true); }}>
                                <span style={{ whiteSpace: 'nowrap' }}>{count}</span>
                              </Button>
                            ) : (
                              <span style={{ whiteSpace: 'nowrap' }}>0</span>
                            );
                          }
                        },
                        {
                          title: t('pickup.progress.columns.expectedParcel'),
                          dataIndex: 'expectedParcel',
                          width: 180, 
                          align: 'left' as const,
                          ellipsis: false,
                          render: (text: number) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                        },
                        { 
                          title: t('pickup.progress.columns.pickedUpParcel'), 
                          dataIndex: 'pickedUpParcel', 
                          width: 180, 
                          align: 'left' as const,
                          ellipsis: false,
                          render: (text: number) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                        },
                        {
                            title: t('pickup.progress.columns.status'),
                            dataIndex: 'status', 
                            width: 140, 
                            align: 'left' as const,
                            ellipsis: false,
                            filters: [
                                { text: 'Draft', value: 'DRAFT' },
                                { text: 'Published', value: 'PUBLISHED' },
                                { text: 'In Progress', value: 'IN_PROGRESS' },
                                { text: 'Completed', value: 'COMPLETED' },
                                { text: 'Cancelled', value: 'CANCELLED' },
                            ],
                            onFilter: (value: any, record: any) => record.status === value,
                            render: (status: string) => {
                                const map: Record<string, { text: string; color: string }> = {
                                    DRAFT: { text: 'Draft', color: 'default' },
                                    PUBLISHED: { text: 'Published', color: 'blue' },
                                    IN_PROGRESS: { text: 'In Progress', color: 'green' },
                                    COMPLETED: { text: 'Completed', color: 'gray' },
                                    CANCELLED: { text: 'Cancelled', color: 'red' },
                                };
                                const v = map[status] || { text: status, color: 'default' };
                                return (
                                    <Tag color={v.color}>
                                        <span style={{ whiteSpace: 'nowrap' }}>{v.text}</span>
                                    </Tag>
                                );
                            }
                        },
                    ]}
                />

                {/* 分页 */}
                <TablePaginationBar
                    total={tableData.length}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    showQuickJumper
                    pageSizeOptions={['10', '20', '50', '100']}
                />
            </div>
            </div>
            <PickupTaskDetailDrawer
                visible={drawerVisible}
                onClose={() => { setDrawerVisible(false); setEditingTaskId(null); }}
                taskId={editingTaskId}
                onSave={() => refresh()}
            />
            <ProgressDownloadModal
                visible={progressDownloadVisible}
                onClose={() => setProgressDownloadVisible(false)}
                fileName={progressFileName}
            />
            <PickupFailedAppointmentDrawer
                visible={failedDrawerVisible}
                onClose={() => setFailedDrawerVisible(false)}
                hubId={currentWarehouseId ? Number(currentWarehouseId) : null}
                defaultDate={taskDateStr}
                mode="failed"
            />
            <PickupFailedAppointmentDrawer
                visible={fakeDrawerVisible}
                onClose={() => setFakeDrawerVisible(false)}
                hubId={currentWarehouseId ? Number(currentWarehouseId) : null}
                defaultDate={taskDateStr}
                mode="fake"
                initialDriver={fakeInitialDriver}
            />
            <PickupFailedAppointmentDrawer
                visible={notOnlineDrawerVisible}
                onClose={() => setNotOnlineDrawerVisible(false)}
                hubId={currentWarehouseId ? Number(currentWarehouseId) : null}
                defaultDate={taskDateStr}
                mode="notOnline"
                initialDriver={notOnlineInitialDriver}
            />
            <PickupFailedAppointmentDrawer
                visible={unscannableDrawerVisible}
                onClose={() => setUnscannableDrawerVisible(false)}
                hubId={currentWarehouseId ? Number(currentWarehouseId) : null}
                defaultDate={taskDateStr}
                mode="unscannable"
            />
            <PartialPickupAppointmentDrawer
                visible={partialPickupDrawerVisible}
                onClose={() => setPartialPickupDrawerVisible(false)}
                hubId={currentWarehouseId ? Number(currentWarehouseId) : null}
                defaultDate={taskDateStr}
                initialDriver={partialPickupInitialDriver}
            />
            </PickupDataProvider>
    );
};

export default ProgressPage;
